import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BadgeCheck, Briefcase, Building, Building2, Factory, Handshake, Map, Plus, Tag, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, toQuery } from "@/lib/api";
import { useAuth, useAuthFeatures } from "@/context/AuthContext";
import { useExpertises, usePersistentView, useUrlState } from "@/hooks/useDirectory";
import { PageHeader, Pager, SearchInput, ViewToggle } from "@/components/common/Layout";
import { ActiveChips, MultiSelectFilter } from "@/components/filters/Filters";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { OrgCard, OrgTable } from "@/components/orgs/OrgViews";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const KEYS = ["org_type", "sector", "department", "relation_types", "expertise"];
const FACETS = [
  { key: "org_type", label: "Type d'organisation", icon: Briefcase },
  { key: "sector", label: "Secteur", icon: Factory },
  { key: "department", label: "Département", icon: Map },
  { key: "relation_types", label: "Relation La Mêlée", icon: Handshake, internal: true },
];
const STATUSES = [
  { key: "member", label: "Adhérent", icon: BadgeCheck },
  { key: "prospect", label: "Prospect", icon: Target, internal: true },
];
const PAGE_SIZE = 24;

const MODES = {
  orgs: {
    base: "/entreprises", title: "Entreprises", icon: Building2, color: "#16A34A",
    add: "Ajouter une entreprise", newPath: "/entreprises/nouvelle",
    description: "Organisations adhérentes, partenaires et prospects de l'écosystème.", unit: "organisation",
  },
  tenants: {
    base: "/locataires", title: "Locataires & Domiciliés", icon: Building, color: "#0284C7",
    add: "Ajouter un locataire / domicilié", newPath: "/locataires/nouveau",
    description: "Retrouvez les entreprises et organisations locataires et domiciliées de La Mêlée.", unit: "organisation",
  },
};

export default function OrgsPage({ mode = "orgs" }) {
  const m = MODES[mode];
  const isTenants = mode === "tenants";
  const { isAuthed } = useAuth();
  const url = useUrlState(KEYS);
  const [view, setView] = usePersistentView(`lm-${mode}-view`);
  const { data: expertises } = useExpertises();
  const features = useAuthFeatures();
  const facetList = FACETS.filter((f) => isAuthed || !f.internal);
  const facets = useQuery({ queryKey: ["org-facets"], queryFn: async () => (await api.get("/organizations/facets")).data });
  const statuses = STATUSES.filter((s) => isAuthed || !s.internal);
  const flags = Object.fromEntries(statuses.map((s) => [s.key, url.sp.get(s.key) === "true" ? "true" : ""]));

  // 3 filtres fonctionnels pour Locataires & Domiciliés
  const tenantChecked = isTenants ? url.sp.get("tenant") !== "false" : false;
  const domiciledChecked = isTenants ? url.sp.get("domiciled") !== "false" : false;
  const memberVal = url.sp.get("member") || "";

  const tenantParams = {
    tenant: tenantChecked ? "true" : "false",
    domiciled: domiciledChecked ? "true" : "false",
    tenants_mode: "true",
    ...(memberVal ? { member: memberVal } : {}),
  };

  const qs = toQuery({
    ...(isTenants ? tenantParams : { ...url.filters, ...flags }),
    q: url.q,
    page: url.page,
    page_size: PAGE_SIZE,
  });

  const bothUnchecked = isTenants && !tenantChecked && !domiciledChecked;

  const list = useQuery({
    queryKey: ["organizations", qs, isAuthed],
    queryFn: async () => {
      if (bothUnchecked) return { items: [], total: 0, page: 1, page_size: PAGE_SIZE };
      return (await api.get(`/organizations?${qs}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items || [];
  const total = list.data?.total || 0;

  const chips = isTenants
    ? [
        ...(memberVal === "true" ? [{ key: "member", value: "true", group: "Adhésion", label: "Adhérent" }] : []),
        ...(memberVal === "false" ? [{ key: "member", value: "false", group: "Adhésion", label: "Non adhérent" }] : []),
        ...(!tenantChecked ? [{ key: "tenant", value: "false", group: "Statut", label: "Locataires masqués" }] : []),
        ...(!domiciledChecked ? [{ key: "domiciled", value: "false", group: "Statut", label: "Domiciliés masqués" }] : []),
      ]
    : [
        ...facetList.flatMap((f) => url.filters[f.key].map((v) => ({ key: f.key, value: v, group: f.label, label: v }))),
        ...url.filters.expertise.map((v) => ({ key: "expertise", value: v, group: "Expertise", label: v })),
        ...statuses.filter((s) => flags[s.key]).map((s) => ({ key: s.key, value: "true", group: "Statut", label: s.label })),
      ];

  const clearTenantsFilters = () => {
    url.setParam("member", "");
    url.setParam("tenant", "");
    url.setParam("domiciled", "");
  };

  let content;
  if (list.isLoading) content = <LoadingGrid count={9} />;
  else if (list.isError) content = <ErrorState onRetry={list.refetch} />;
  else if (bothUnchecked) {
    content = (
      <EmptyState
        title="Aucun type sélectionné"
        description="Cochez « Locataire » ou « Domicilié » ci-dessus pour afficher des structures."
        action={
          <Button variant="outline" className="rounded-full" onClick={() => { url.setParam("tenant", "true"); url.setParam("domiciled", "true"); }}>
            Tout afficher
          </Button>
        }
      />
    );
  } else if (!items.length) {
    content = <EmptyState description="Aucune organisation ne correspond à ces critères."
      action={isAuthed && <Button asChild className="rounded-full"><Link to={m.newPath}>{m.add}</Link></Button>} />;
  } else if (view === "table") content = <OrgTable orgs={items} basePath={m.base} />;
  else content = <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="orgs-grid">{items.map((o) => <OrgCard key={o.id} org={o} basePath={m.base} />)}</div>;

  return (
    <div className="lm-container pb-20">
      <PageHeader eyebrow="Répertoire" title={m.title} icon={m.icon} color={m.color}
        description={m.description}
        actions={isAuthed && (
          <Button asChild className="rounded-full px-5 font-semibold" data-testid={mode === "tenants" ? "add-tenant-button" : "add-org-button"}>
            <Link to={m.newPath}><Plus className="h-4 w-4" /> {m.add}</Link>
          </Button>
        )} />
      <div className="lm-card space-y-4 p-4 sm:p-5" data-testid="orgs-filter-bar">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <SearchInput className="flex-1" value={url.q} onChange={(v) => url.setParam("q", v)} testId="orgs-search-input" placeholder="Nom, secteur, type, bureau…" />
          <ViewToggle value={view} onChange={setView} />
        </div>
        {isTenants ? (
          <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="tenants-filters">
            {/* 1. Adhésion */}
            <div className="flex items-center gap-1 rounded-full border border-border bg-stone-50 p-1 text-xs font-semibold">
              <span className="px-2 text-stone-500">Adhésion :</span>
              <button
                type="button"
                onClick={() => url.setParam("member", "")}
                className={cn("rounded-full px-3 py-1 transition-colors", !memberVal ? "bg-primary text-white shadow-sm" : "text-stone-600 hover:bg-stone-200/60")}
                data-testid="filter-membership-all"
              >
                Tous
              </button>
              <button
                type="button"
                onClick={() => url.setParam("member", "true")}
                className={cn("rounded-full px-3 py-1 transition-colors", memberVal === "true" ? "bg-primary text-white shadow-sm" : "text-stone-600 hover:bg-stone-200/60")}
                data-testid="filter-membership-adherent"
              >
                Adhérent
              </button>
              <button
                type="button"
                onClick={() => url.setParam("member", "false")}
                className={cn("rounded-full px-3 py-1 transition-colors", memberVal === "false" ? "bg-primary text-white shadow-sm" : "text-stone-600 hover:bg-stone-200/60")}
                data-testid="filter-membership-non-adherent"
              >
                Non adhérent
              </button>
            </div>

            <span className="hidden h-6 w-px bg-border sm:block" />

            {/* 2. Locataire */}
            <label className={cn(
              "inline-flex cursor-pointer select-none items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors",
              tenantChecked ? "border-sky-300 bg-sky-50 text-sky-800" : "border-border bg-white text-stone-600 hover:bg-stone-50"
            )}>
              <Checkbox
                checked={tenantChecked}
                onCheckedChange={(c) => url.setParam("tenant", c ? "true" : "false")}
                data-testid="filter-tenant-checkbox"
              />
              <Building className="h-4 w-4" />
              <span>Locataire</span>
            </label>

            {/* 3. Domicilié */}
            <label className={cn(
              "inline-flex cursor-pointer select-none items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors",
              domiciledChecked ? "border-indigo-300 bg-indigo-50 text-indigo-800" : "border-border bg-white text-stone-600 hover:bg-stone-50"
            )}>
              <Checkbox
                checked={domiciledChecked}
                onCheckedChange={(c) => url.setParam("domiciled", c ? "true" : "false")}
                data-testid="filter-domiciled-checkbox"
              />
              <Building2 className="h-4 w-4" />
              <span>Domicilié</span>
            </label>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {statuses.map((s) => (
              <button key={s.key} type="button" onClick={() => url.setParam(s.key, flags[s.key] ? "" : "true")} data-testid={`filter-status-${s.key}`}
                aria-pressed={!!flags[s.key]}
                className={cn("inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors",
                  flags[s.key] ? "border-primary/40 bg-accent text-primary" : "border-border bg-white text-stone-700 hover:bg-stone-50")}>
                <s.icon className="h-3.5 w-3.5" /> {s.label}
              </button>
            ))}
            <span className="mx-1 hidden h-6 w-px bg-border sm:block" />
            {facetList.map((f) => (
              <MultiSelectFilter key={f.key} label={f.label} icon={f.icon} selected={url.filters[f.key]} onChange={(v) => url.setValues(f.key, v)}
                options={(facets.data?.[f.key] || []).map((v) => ({ value: v, label: v }))} testId={`filter-${f.key}`} />
            ))}
            {features.org_expertises && <MultiSelectFilter label="Expertise" icon={Tag} selected={url.filters.expertise} onChange={(v) => url.setValues("expertise", v)}
              options={(expertises || []).map((e) => ({ value: e.name, label: e.name, color: e.color }))} testId="filter-expertise" />}
          </div>
        )}
        <ActiveChips chips={chips} onClear={isTenants ? clearTenantsFilters : url.clearAll}
          onRemove={(chip) => {
            if (isTenants) {
              if (chip.key === "member") url.setParam("member", "");
              else if (chip.key === "tenant") url.setParam("tenant", "true");
              else if (chip.key === "domiciled") url.setParam("domiciled", "true");
            } else {
              ["member", "prospect"].includes(chip.key) ? url.setParam(chip.key, "") : url.removeValue(chip.key, chip.value);
            }
          }} />
      </div>
      <p className="mb-4 mt-6 text-sm text-stone-600" data-testid="results-count">
        <b className="font-heading text-lg text-stone-900">{formatNumber(total)}</b> {m.unit}{total > 1 ? "s" : ""}
      </p>
      <div className={list.isFetching && !list.isLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>{content}</div>
      <Pager page={url.page} total={total} pageSize={PAGE_SIZE} onPage={url.setPage} />
    </div>
  );
}
